import {emptyObject} from '@packages/utilities/fp/utils';
import {createNodeStyleForItem} from './createNodeStyleForItem';

// FIXME: Should we merge `@packages/systems/style/types` and
// `@packages/systems/core/utils/StyleTypes`?
import {
  getBreakpointID,
  type BreakpointID,
} from '@packages/systems/style/types';
import {
  getPseudoStateKey,
  type PseudoStateKey,
} from '@packages/systems/core/utils/StyleTypes';

import {
  type ExpressionPath,
  matchExpression,
  Expressions,
} from '@packages/systems/wfdl';
import type {Json} from '@packages/utilities/json';
import type {
  StyleData,
  BreakpointData,
  PseudoStateData,
} from './createNodeStyleForItem';

import {
  ERecord,
  type Expression,
  type ExpressionElement,
} from '@packages/systems/wfdl-ast';
import {
  isVariantStateKey,
  VariantStateKey,
} from '@packages/systems/style-legacy/selectors';

const expressionToStyleData: (arg1: Expression) => StyleData = (() => {
  const expressionToPojo: (arg1: Expression) => Json = matchExpression<Json>({
    Boolean: (bool) => bool,
    Number: (num) => num,
    Text: (str) => str,
    List: (items) => items.map(expressionToPojo),
    Record: (fields) => {
      const result: Record<string, any> = {};
      for (const k of Object.keys(fields)) {
        const field = fields[k];
        if (field !== undefined) {
          result[k] = expressionToPojo(field);
        }
      }
      return result;
    },

    _: (_) => {
      throw new TypeError('Expected Record, List, Boolean, Number, or Text');
    },
  });

  const expressionToPseudoStateData: (arg1: Expression) => PseudoStateData =
    matchExpression({
      Record: (fields) => {
        const fieldNames = new Set(Object.keys(fields));
        if (fieldNames.size === 0) {
          return emptyObject;
        }

        const result: PseudoStateData = {};
        for (const k of Object.keys(fields)) {
          const pseudoStateKey = getPseudoStateKey(k);
          if (!pseudoStateKey) {
            throw new TypeError(`Unknown pseudo-state key: ${k}`);
          }

          // @ts-expect-error - TS2322 - Type 'Json' is not assignable to type 'Record<any, any> | undefined'.
          result[pseudoStateKey] = expressionToPojo(fields[pseudoStateKey]);
        }
        return result;
      },
      _: (_) => {
        throw new TypeError('expressionToPseudoStateData: Expected Record');
      },
    });

  const expressionToBreakpointObject: (arg1: Expression) => BreakpointData =
    matchExpression({
      Record: (fields) => {
        const fieldNames = new Set(Object.keys(fields));
        if (fieldNames.size === 0) {
          return emptyObject;
        }

        const result: BreakpointData = {};
        for (const k of Object.keys(fields)) {
          const breakpointID = getBreakpointID(k);
          if (!breakpointID) {
            throw new TypeError(`Unknown breakpoint key: ${k}`);
          }

          result[breakpointID] = expressionToPseudoStateData(
            // @ts-expect-error - TS2345 - Argument of type 'Expression | undefined' is not assignable to parameter of type 'Expression'.
            fields[breakpointID]
          );
        }
        return result;
      },
      _: (_) => {
        throw new TypeError('expressionToBreakpointObject: Expected Record');
      },
    });

  const expressionToVariantStateKeyObject: (
    arg1: Expression
  ) => Record<VariantStateKey, BreakpointData> = matchExpression({
    Record: (fields) => {
      const fieldNames = Object.keys(fields);
      if (fieldNames.length === 0) {
        return emptyObject;
      }

      const result: Record<VariantStateKey, BreakpointData> = {};
      for (const k of fieldNames) {
        const field = fields[k];
        if (field === undefined) continue;

        if (!isVariantStateKey(k)) {
          throw new TypeError(`Invalid variant state key: ${k}`);
        }

        result[k] = expressionToBreakpointObject(field);
      }
      return result;
    },
    _: (_) => {
      throw new TypeError('expressionToVariantStateKeyObject: Expected Record');
    },
  });

  return matchExpression({
    Record: (fields) => {
      const hasBase = fields.hasOwnProperty('base');
      const hasVariants = fields.hasOwnProperty('variants');

      if (!hasBase && !hasVariants) {
        return emptyObject;
      }

      const result: Partial<StyleData> = {};

      if (hasBase) {
        // FIXME: Right now, there are cases where `style` data being passed in
        // here is not of the expected `StyleData` shape; i.e. for some layout
        // presets we have direct style POJOs in the element's `data.style`,
        // rather than the `{base: {<pseudo>: {<mq>: ...}}}` shape (See
        // `LayoutMainContent` for some examples of this).
        //
        // For backwards compatibility, we interpret these as `{}` rather than
        // `throw`ing. This only works because `createNodeStyleForItem` ignores
        // unexpected keys so it is effectively equivalent.
        //
        // Should we instead return these POJOs directly when we don't see the
        // `base` shape, so this function produces the "applied" style (which
        // doesn't need to be computed/flattened based on current pseudo/MQ)?
        result.base = expressionToBreakpointObject(fields.base!);
      }

      if (hasVariants) {
        result.variants = expressionToVariantStateKeyObject(fields.variants!);
      }

      return result;
    },
    _: (_) => {
      throw new TypeError('Expected Record');
    },
  });
})();

const emptyRecord = ERecord(emptyObject);
const STYLE_EXPRESSION_PATH: ExpressionPath = [{in: 'Record', at: 'style'}];

export const getAppliedElementStyle = (
  el: ExpressionElement,
  mediaQuery: BreakpointID,
  pseudoMode: null | PseudoStateKey,
  variantState: VariantStateKey | null
): Record<any, any> => {
  const nodeStyleExpr: Expression =
    Expressions.getIn(Expressions.getElementData(el), STYLE_EXPRESSION_PATH) ??
    emptyRecord;

  const stylePojo: StyleData =
    nodeStyleExpr === emptyRecord
      ? emptyObject
      : expressionToStyleData(nodeStyleExpr);

  return createNodeStyleForItem(mediaQuery)(pseudoMode || 'noPseudo')(
    variantState
  )(stylePojo);
};
